import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  List,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconCheck, IconRefresh } from "@tabler/icons-react";
import type { DeletionEvidence, DeletionStep } from "@/api/accountDeletion";
import { DetailGrid } from "@/components/DetailGrid";
import { Mono } from "@/components/Mono";
import { PageHeader } from "@/components/PageHeader";
import { Timestamp } from "@/components/Timestamp";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { entityPath, PATHS } from "@/routes/paths";
import { humanize } from "@/lib/format";
import { isPast } from "@/lib/datetime";
import { FinalizeDialog, ReviewDialog } from "./DeletionDialogs";
import { ResponseTarget } from "./AccountDeletionQueueView";
import { QueueFooter, QueueState } from "./QueueParts";
import {
  blockerLabel,
  currentBlockers,
  nextAction,
  outcomeLabel,
  reasonLabel,
  reviewNeedsRecheck,
  STATE_LABELS,
  STEP_LABELS,
} from "./presentation";
import type { AccountDeletionDetailViewProps } from "./types";

function Outcome({ evidence, step }: { evidence: DeletionEvidence | null; step: DeletionStep }) {
  return (
    <Badge
      variant="light"
      tt="none"
      color={
        evidence?.outcome === "erased"
          ? "teal"
          : evidence?.outcome === "retained"
            ? "orange"
            : evidence?.outcome === "ready"
              ? "indigo"
              : "gray"
      }
    >
      {outcomeLabel(step, evidence?.outcome ?? null)}
    </Badge>
  );
}

export function AccountDeletionDetailView(props: AccountDeletionDetailViewProps) {
  const [review, setReview] = useState<{ step: DeletionStep; updatedAt: string } | null>(null);
  const [finalizeVersion, setFinalizeVersion] = useState<string | null>(null);
  const { data } = props;
  const crumbs = [{ label: "Account deletion", to: PATHS.accountDeletions }, { label: "Request" }];
  if (!data)
    return (
      <>
        <PageHeader
          title="Deletion request"
          crumbs={crumbs}
          actions={
            <Button
              size="xs"
              variant="default"
              leftSection={<IconRefresh size={14} />}
              loading={props.loading}
              onClick={props.onRefresh}
            >
              Refresh
            </Button>
          }
        />
        {props.loading ? (
          <LoadingState />
        ) : props.error ? (
          <ErrorState error={props.error} />
        ) : (
          <EmptyState title="Request not found" description="Check the request or account ID." />
        )}
      </>
    );

  const { request } = data;
  const inactive = props.loading || props.stale || !!props.error || props.actionPending;
  const canFinalize = data.canFinalize && props.canStartErasure && !inactive;
  const blockers = currentBlockers(data);
  const reviewDisabledReason = !props.canReview
    ? "You do not have permission to save reviews."
    : inactive
      ? "Wait for the latest details before saving a review."
      : "";
  const finalizeDisabledReason = !props.canStartErasure
    ? "You do not have permission to start deletion."
    : inactive
      ? "Wait for the latest checks."
      : !data.canFinalize
        ? "See the remaining checks below."
        : "";

  return (
    <>
      <PageHeader
        title="Deletion request"
        subtitle={
          <Text span ff="monospace" size="sm">
            {request.id}
          </Text>
        }
        crumbs={crumbs}
        actions={
          <Button
            size="xs"
            variant="default"
            leftSection={<IconRefresh size={14} />}
            loading={props.loading}
            onClick={props.onRefresh}
            disabled={props.actionPending}
          >
            Refresh
          </Button>
        }
      />
      <Stack gap="lg">
        {props.error ? <ErrorState error={props.error} /> : null}
        {props.stale ? (
          <Alert color="yellow">
            Updating this request. Actions will be available when the latest checks arrive.
          </Alert>
        ) : null}
        <Card withBorder padding="lg">
          <Group justify="space-between" align="flex-start" mb="lg">
            <Stack gap="xs" style={{ flex: 1 }}>
              <Badge
                variant="light"
                tt="none"
                color={request.state === "completed" ? "teal" : "indigo"}
                w="fit-content"
              >
                {STATE_LABELS[request.state]}
              </Badge>
              <Text size="sm" c="dimmed">
                Next step
              </Text>
              <Text fw={600}>{nextAction(data)}</Text>
            </Stack>
            {request.state !== "erasing" && request.state !== "completed" ? (
              <Tooltip
                label={finalizeDisabledReason}
                disabled={!finalizeDisabledReason}
                multiline
                w={260}
              >
                <span>
                  <Button
                    color="red"
                    variant="light"
                    disabled={!canFinalize}
                    onClick={() => setFinalizeVersion(request.updatedAt)}
                  >
                    Start deletion
                  </Button>
                </span>
              </Tooltip>
            ) : null}
          </Group>
          <DetailGrid
            fields={[
              {
                label: "Account",
                value: (
                  <Anchor
                    component={Link}
                    to={entityPath("user", request.user)}
                    size="sm"
                    ff="monospace"
                  >
                    {request.user}
                  </Anchor>
                ),
              },
              { label: "Requested", value: <Timestamp value={request.requestedAt} absolute /> },
              {
                label: "First reply target",
                value: (
                  <ResponseTarget
                    value={request.responseDueAt}
                    completed={request.state === "completed"}
                  />
                ),
              },
            ]}
          />
          <Text size="xs" c="dimmed" mt="sm">
            See the private case for deletion deadlines.
          </Text>
          {blockers.length && request.state !== "completed" ? (
            <Accordion variant="contained" mt="lg">
              <Accordion.Item value="blockers">
                <Accordion.Control>What needs attention ({blockers.length})</Accordion.Control>
                <Accordion.Panel>
                  <List size="sm" spacing="xs">
                    {blockers.map((item, index) => (
                      <List.Item key={`${item.code}-${item.domain}-${item.step}-${index}`}>
                        {blockerLabel(item)}
                      </List.Item>
                    ))}
                  </List>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          ) : null}
        </Card>

        <div>
          <Text fw={600} mb="sm">
            Account access
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2, xl: 4 }}>
            {data.domains.map(({ domain, assessment }) => (
              <Card key={domain} withBorder padding="md">
                <Group justify="space-between" mb="sm">
                  <Text fw={600}>{STEP_LABELS[domain]}</Text>
                  <Badge
                    variant="light"
                    tt="none"
                    color={assessment?.access === "closed" ? "teal" : "gray"}
                  >
                    {assessment?.access === "closed"
                      ? "Access closed"
                      : assessment?.access === "closing"
                        ? "Closing"
                        : "Waiting for confirmation"}
                  </Badge>
                </Group>
                <Text size="sm">
                  {assessment?.status === "reviewRequired"
                    ? "Records to review"
                    : assessment?.status === "clear"
                      ? "No issues found"
                      : assessment
                        ? humanize(assessment.status)
                        : "Waiting for the first check"}
                </Text>
                {assessment?.reasonCodes.length ? (
                  <Accordion variant="default" mt="sm">
                    <Accordion.Item value="records">
                      <Accordion.Control>What to review</Accordion.Control>
                      <Accordion.Panel>
                        <List size="xs" spacing={4}>
                          {assessment.reasonCodes.map((code) => (
                            <List.Item key={code}>
                              <Tooltip label={code}>
                                <span>{reasonLabel(code)}</span>
                              </Tooltip>
                            </List.Item>
                          ))}
                        </List>
                      </Accordion.Panel>
                    </Accordion.Item>
                  </Accordion>
                ) : null}
                {assessment ? (
                  <Text size="xs" c="dimmed" mt="sm">
                    Checked <Timestamp value={assessment.updatedAt} />
                  </Text>
                ) : null}
                {domain === "wallet" && assessment?.backupErased ? (
                  <Group gap={4} mt="xs">
                    <IconCheck size={14} color="var(--mantine-color-teal-6)" />
                    <Text size="xs">Recovery backups removed</Text>
                  </Group>
                ) : null}
              </Card>
            ))}
          </SimpleGrid>
        </div>

        <Card withBorder padding="lg">
          <Text fw={600}>Reviews</Text>
          <Text size="sm" c="dimmed" mt={4} mb="md">
            Save each result after verifying the work. Provider cleanup and sending notices happen
            outside this screen.
          </Text>
          <Table.ScrollContainer minWidth={800}>
            <Table verticalSpacing="md" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Category</Table.Th>
                  <Table.Th>Result</Table.Th>
                  <Table.Th>Case reference / data kept</Table.Th>
                  <Table.Th>Action</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.reviews.map(({ step, evidence }) => {
                  const completedLocked =
                    request.state === "completed" && evidence?.outcome !== "retained";
                  const needsRecheck = reviewNeedsRecheck(data, step);
                  return (
                    <Table.Tr key={step}>
                      <Table.Td>
                        <Text fw={500} size="sm">
                          {STEP_LABELS[step]}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {needsRecheck ? (
                          <Badge variant="light" tt="none" color="orange">
                            Recheck needed
                          </Badge>
                        ) : (
                          <Outcome step={step} evidence={evidence} />
                        )}
                      </Table.Td>
                      <Table.Td>
                        {evidence ? (
                          <Stack gap={4} maw={430}>
                            <Text size="sm" style={{ overflowWrap: "anywhere" }}>
                              {evidence.evidenceReference}
                            </Text>
                            {evidence.outcome === "retained" ? (
                              <>
                                <Text size="xs" c="dimmed">
                                  {evidence.retentionPolicy}
                                </Text>
                                <Text
                                  size="xs"
                                  c={isPast(evidence.retentionUntil) ? "orange" : "dimmed"}
                                >
                                  Keep until / review on:{" "}
                                  <Timestamp value={evidence.retentionUntil} absolute />
                                </Text>
                              </>
                            ) : null}
                            <Text size="xs" c="dimmed">
                              Reviewed <Timestamp value={evidence.createdAt} />
                            </Text>
                          </Stack>
                        ) : (
                          <Text c="dimmed" size="sm">
                            No review recorded
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Tooltip
                          label={
                            completedLocked
                              ? "Complete. Only data kept under a policy can be reviewed again."
                              : reviewDisabledReason
                          }
                          disabled={!completedLocked && !reviewDisabledReason}
                          multiline
                          w={280}
                        >
                          <span>
                            <Button
                              size="xs"
                              variant="default"
                              disabled={!props.canReview || inactive || completedLocked}
                              onClick={() => setReview({ step, updatedAt: request.updatedAt })}
                            >
                              {step === "receipt"
                                ? data.canRecordFinalNotice
                                  ? evidence?.outcome === "erased"
                                    ? "Update delivery"
                                    : "Record delivery"
                                  : "Review notice"
                                : needsRecheck
                                  ? "Review again"
                                  : evidence
                                    ? "Update review"
                                    : "Review"}
                            </Button>
                          </span>
                        </Tooltip>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>

        <Accordion variant="separated">
          <Accordion.Item value="evidence">
            <Accordion.Control>Review history</Accordion.Control>
            <Accordion.Panel>
              <QueueState queue={props.evidence} emptyTitle="No evidence recorded" />
              {props.evidence.items.length ? (
                <Table.ScrollContainer minWidth={850}>
                  <Table verticalSpacing="sm">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Recorded</Table.Th>
                        <Table.Th>Category / result</Table.Th>
                        <Table.Th>Case reference</Table.Th>
                        <Table.Th>Reviewer</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {props.evidence.items.map((item) => (
                        <Table.Tr key={item.id}>
                          <Table.Td>
                            <Timestamp value={item.createdAt} absolute />
                          </Table.Td>
                          <Table.Td>
                            <Stack gap={4}>
                              <Text size="sm">{STEP_LABELS[item.step]}</Text>
                              <Outcome evidence={item} step={item.step} />
                            </Stack>
                          </Table.Td>
                          <Table.Td>
                            <Stack gap={4} maw={420}>
                              <Text size="sm" style={{ overflowWrap: "anywhere" }}>
                                {item.evidenceReference}
                              </Text>
                              {item.outcome === "retained" ? (
                                <>
                                  <Text size="xs" c="dimmed">
                                    {item.retentionPolicy}
                                  </Text>
                                  <Timestamp value={item.retentionUntil} absolute />
                                </>
                              ) : null}
                            </Stack>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs" ff="monospace" style={{ overflowWrap: "anywhere" }}>
                              {item.reviewedBy}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              ) : null}
              <QueueFooter queue={props.evidence} />
            </Accordion.Panel>
          </Accordion.Item>
          <Accordion.Item value="actions">
            <Accordion.Control>Activity history</Accordion.Control>
            <Accordion.Panel>
              <QueueState queue={props.actions} emptyTitle="No operator actions recorded" />
              {props.actions.items.length ? (
                <Table.ScrollContainer minWidth={800}>
                  <Table verticalSpacing="sm">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Recorded</Table.Th>
                        <Table.Th>Action</Table.Th>
                        <Table.Th>Case reference</Table.Th>
                        <Table.Th>Recorded by</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {props.actions.items.map((item) => (
                        <Table.Tr key={item.id}>
                          <Table.Td>
                            <Timestamp value={item.createdAt} absolute />
                          </Table.Td>
                          <Table.Td>{humanize(item.action)}</Table.Td>
                          <Table.Td>
                            <Text size="sm" style={{ overflowWrap: "anywhere" }}>
                              {item.evidenceReference}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs" ff="monospace" style={{ overflowWrap: "anywhere" }}>
                              {item.actor}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              ) : null}
              <QueueFooter queue={props.actions} />
            </Accordion.Panel>
          </Accordion.Item>
          <Accordion.Item value="case-details">
            <Accordion.Control>Request details</Accordion.Control>
            <Accordion.Panel>
              <DetailGrid
                fields={[
                  {
                    label: "Last updated",
                    value: <Timestamp value={request.updatedAt} absolute />,
                  },
                  {
                    label: "Identity cleanup confirmed",
                    value: <Timestamp value={data.identityErasedAt} absolute />,
                  },
                  { label: "Completed", value: <Timestamp value={request.completedAt} absolute /> },
                  { label: "Confirmation version", value: request.confirmationVersion },
                  ...(data.finalization
                    ? [
                        {
                          label: "Deletion approved by",
                          value: <Mono value={data.finalization.actor} />,
                        },
                        {
                          label: "Approval reference",
                          value: data.finalization.evidenceReference,
                        },
                      ]
                    : []),
                ]}
              />
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Stack>
      {review ? (
        <ReviewDialog
          data={data}
          step={review.step}
          expectedUpdatedAt={review.updatedAt}
          disabled={!props.canReview || inactive}
          pending={props.actionPending}
          onClose={() => setReview(null)}
          onReview={props.onReview}
        />
      ) : null}
      {finalizeVersion ? (
        <FinalizeDialog
          data={data}
          expectedUpdatedAt={finalizeVersion}
          disabled={!props.canStartErasure || inactive}
          pending={props.actionPending}
          onClose={() => setFinalizeVersion(null)}
          onFinalize={props.onFinalize}
        />
      ) : null}
    </>
  );
}
